<template>
  <main class="rule-page" :class="localeClass">
    <section ref="ruleBlockRef" class="rule-block">
      <div class="row row-center" style="height: 100%">
        <div class="col-12-sm col-9" style="position: relative">
          <div class="text-wrap">
            <h1>{{ $t('rule.pageTitle') }}</h1>
            <span>{{ $t('rule.pageIntro') }}</span>
          </div>
        </div>
      </div>
    </section>

    <section class="rule-content">
      <div class="row row-center">
        <div class="col-12-sm col-9">
          <div class="row rule-content-inner">
            <aside class="col-12-sm col-3 rule-navigation" :aria-label="$t('rule.contentsLabel')" data-aos="fade">
              <div class="navigation-title">
                <span class="navigation-title-desktop">{{ $t('rule.contents') }}</span>
              </div>
              <button
                type="button"
                class="navigation-toggle"
                :class="{ 'is-open': isRuleNavigationOpen }"
                :aria-expanded="isRuleNavigationOpen"
                aria-controls="rule-navigation-list"
                @click="isRuleNavigationOpen = !isRuleNavigationOpen"
              >
                <span>{{ selectedRuleTitle }}</span>
                <span class="navigation-toggle-icon" aria-hidden="true"></span>
              </button>
              <nav id="rule-navigation-list" :class="{ 'is-open': isRuleNavigationOpen }">
                <a
                  v-for="(item, index) in ruleSections"
                  :key="item.id"
                  :href="`#${item.id}`"
                  class="navigation-link"
                  :class="{ 'is-selected': selectedRuleId === item.id }"
                  :aria-current="selectedRuleId === item.id ? 'location' : undefined"
                  @click.prevent="selectRuleSection(item.id)"
                >
                  <span>{{ String(index + 1).padStart(2, '0') }}</span>
                  {{ item.title }}
                </a>
              </nav>
            </aside>

            <div class="col-12-sm col-9 rule-details" data-aos="fade">
              <header class="rule-intro">
                <h2>{{ $t('rule.introTitle') }}</h2>
                <p>{{ $t('rule.introText') }}</p>
              </header>

              <div class="rule-highlights">
                <article v-for="highlight in ruleHighlights" :key="highlight.title" class="highlight-card">
                  <span class="highlight-number">{{ highlight.number }}</span>
                  <h3>{{ highlight.title }}</h3>
                  <p>{{ highlight.description }}</p>
                </article>
              </div>

              <div class="rule-notice">
                <h3>{{ $t('rule.noticeTitle') }}</h3>
                <p>{{ $t('rule.noticeText') }}</p>
              </div>

              <div class="rule-list">
                <article
                  v-for="(item, index) in ruleSections"
                  :id="item.id"
                  :key="item.id"
                  class="rule-item"
                  :class="{ 'is-selected': selectedRuleId === item.id }"
                >
                  <div class="rule-item-number">{{ String(index + 1).padStart(2, '0') }}</div>
                  <div class="rule-item-body">
                    <h3>{{ item.title }}</h3>
                    <p v-for="paragraph in item.paragraphs" :key="paragraph" v-html="paragraph"></p>
                  </div>
                </article>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  </main>
</template>
<script lang="ts" setup>
import { computed, ref, nextTick, onMounted, onUnmounted } from 'vue';
const ruleBlockRef = ref<HTMLElement | null>(null);
const { t, locale } = useI18n();
const localeClass = computed(() => `is-${locale.value}`);

const ruleHighlights = computed(() => [
  {
    number: '01',
    title: t('rule.highlight1Title'),
    description: t('rule.highlight1Description'),
  },
  {
    number: '02',
    title: t('rule.highlight2Title'),
    description: t('rule.highlight2Description'),
  },
  {
    number: '03',
    title: t('rule.highlight3Title'),
    description: t('rule.highlight3Description'),
  },
]);

const ruleSections = computed(() => [
  { id: 'rule-01', title: t('rule.section1Title'), paragraphs: [t('rule.section1Text')] },
  { id: 'rule-02', title: t('rule.section2Title'), paragraphs: [t('rule.section2Text')] },
  { id: 'rule-03', title: t('rule.section3Title'), paragraphs: [t('rule.section3Text')] },
  { id: 'rule-04', title: t('rule.section4Title'), paragraphs: [t('rule.section4Text')] },
  { id: 'rule-05', title: t('rule.section5Title'), paragraphs: [t('rule.section5Text')] },
  { id: 'rule-06', title: t('rule.section6Title'), paragraphs: [t('rule.section6Text')] },
  { id: 'rule-07', title: t('rule.section7Title'), paragraphs: [t('rule.section7Text')] },
  { id: 'rule-08', title: t('rule.section8Title'), paragraphs: [t('rule.section8Text')] },
  { id: 'rule-09', title: t('rule.section9Title'), paragraphs: [t('rule.section9Text')] },
  { id: 'rule-10', title: t('rule.section10Title'), paragraphs: [t('rule.section10Text')] },
  { id: 'rule-11', title: t('rule.section11Title'), paragraphs: [t('rule.section11Text')] },
  { id: 'rule-12', title: t('rule.section12Title'), paragraphs: [t('rule.section12Text')] },
]);

const isRuleNavigationOpen = ref(false);
const selectedRuleId = ref('rule-01');
const selectedRuleTitle = computed(
  () => ruleSections.value.find((item) => item.id === selectedRuleId.value)?.title ?? t('rule.section1Title')
);

const selectRuleSection = async (id: string) => {
  selectedRuleId.value = id;
  isRuleNavigationOpen.value = false;

  await nextTick();

  document.getElementById(id)?.scrollIntoView({
    behavior: 'smooth',
    block: 'start',
  });
};

const handleParallaxScroll = () => {
  if (!ruleBlockRef.value) return;

  const scrolled = window.scrollY;
  const parallaxSpeed = 0.5; // 調整這個值來控制視差速度 (0.5 = 背景移動速度是正常的一半)

  // 透過 CSS 變數來移動 ::before 偽元素的背景圖片
  ruleBlockRef.value.style.setProperty('--bg-y', `${scrolled * parallaxSpeed}px`);
};

onMounted(() => {
  window.addEventListener('scroll', handleParallaxScroll, { passive: true });
  handleParallaxScroll();
});

onUnmounted(() => {
  window.removeEventListener('scroll', handleParallaxScroll);
});
</script>
<style lang="scss" scoped>
@use '@/assets/css/main.scss' as *;
section {
  margin-bottom: 30px;
  &:last-child {
    margin-bottom: 0;
  }
}
.rule-block {
  height: 800px;
  background-color: $color-bg-dark02;
  position: relative;

  &::before {
    content: '';
    position: absolute;
    inset: 0;
    background: url(@/assets/img/ruleBanner.png);
    background-size: cover;
    background-position: center;
    background-repeat: no-repeat;
    background-position-y: calc(var(--bg-y, 0px) - 180px);
    filter: grayscale(100%);
    pointer-events: none;
    z-index: 0;
    @media screen and (max-width: $breakpoint-med) {
      background-position-y: calc(var(--bg-y, 0px) - 140px);
      filter: grayscale(100%);
    }
    @media screen and (max-width: $breakpoint-small) {
      background-position-y: calc(var(--bg-y, 0px) - 100px);
      filter: grayscale(100%);
    }
  }

  &::after {
    content: '';
    position: absolute;
    inset: 0;
    background: linear-gradient(
      to bottom,
      rgba($color-bg-dark, 0.96) 0%,
      rgba($color-bg-dark, 0.8) 14%,
      rgba($color-bg-dark, 0.35) 42%,
      rgba($color-bg-dark, 0) 62%
    );
    pointer-events: none;
    z-index: 1;
  }

  > .row {
    position: relative;
    z-index: 2;
  }

  @media screen and (max-width: $breakpoint-xlarge) {
    height: auto;
    padding-top: 700px;
  }
  @media screen and (max-width: $breakpoint-med) {
    padding-top: 300px;
  }
  @media screen and (max-width: $breakpoint-small) {
    padding-top: 300px;
  }
  @media screen and (max-width: $breakpoint-mini) {
    padding-top: 300px;
  }
  .text-wrap {
    position: absolute;
    bottom: 100px;
    left: 50%;
    transform: translateX(-50%);
    width: 100%;
    span {
      color: $color-white;
      font-size: 15px;
    }
    @media screen and (max-width: $breakpoint-med) {
      width: 90%;
      bottom: 0;
      text-align: center;
    }
  }
  h1 {
    font-size: 40px;
    color: $color-white;
    font-weight: bold;
    margin-bottom: 30px;
    @media screen and (max-width: $breakpoint-med) {
      font-size: 30px;
    }
    @media screen and (max-width: $breakpoint-small) {
      font-size: 26px;
      margin-bottom: 20px;
    }
  }
}

.rule-content {
  background: $color-bg-dark02;
  color: rgba($color-white, 0.82);
  font-size: 13px;
  line-height: 26px;
  padding: 72px 0 120px;
}

.rule-content-inner {
  width: calc(100% + 4%);
  margin-left: -2%;
}

.rule-navigation {
  position: sticky;
  top: 32px;
  align-self: start;

  .navigation-toggle {
    display: none;
  }

  .navigation-title {
    display: flex;
    align-items: center;
    justify-content: center;
    min-height: 30px;
    margin-bottom: 18px;
    color: $color-text-dark;
    background: $color-bg-yellow;
    border-radius: 4px;
    font-size: 14px;
    font-weight: 700;
    letter-spacing: 0.08em;
    padding: 6px 0;
  }

  nav {
    display: flex;
    flex-direction: column;
    gap: 12px;
  }

  .navigation-link {
    display: grid;
    grid-template-columns: 28px 1fr;
    gap: 2px;
    color: rgba($color-white, 0.78);
    font-size: 14px;
    line-height: 26px;
    transition: color 0.25s ease;

    span {
      color: $color-primary;
      font-size: 11px;
      font-weight: bold;
    }

    &:hover,
    &:focus-visible,
    &.is-selected {
      color: $color-primary;
    }
  }
}

.rule-details {
  min-width: 0;
}

.rule-intro {
  margin-bottom: 24px;

  h2 {
    color: $color-primary;
    font-size: 25px;
    font-weight: 700;
    line-height: 1.25;
    letter-spacing: 0.05em;
    margin-bottom: 8px;
  }

  p {
    color: rgba($color-white, 0.7);
    font-size: 13px;
    line-height: 26px;
  }
}

.rule-highlights {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 10px;
  margin-bottom: 24px;
}

.highlight-card {
  min-height: 100px;
  padding: 12px 12px 40px;
  border: 1px solid rgba($color-primary, 0.75);
  border-radius: 5px;
  background: rgba($color-bg-dark, 0.2);
  text-align: center;
  @media only screen and (max-width: $breakpoint-med) {
    padding: 12px;
  }
  .highlight-number {
    display: block;
    color: $color-primary;
    font-size: 12px;
    text-align: left;
  }

  h3 {
    margin: 5px 0 6px;
    color: $color-primary;
    font-size: 17px;
    font-weight: 700;
  }

  p {
    color: rgba($color-white, 0.7);
    font-size: 13px;
    line-height: 26px;
  }
}

.rule-notice {
  margin-bottom: 26px;
  padding: 28px 18px;
  border-left: 2px solid $color-primary;
  background: #151515;

  h3 {
    margin-bottom: 6px;
    color: $color-primary;
    font-size: 17px;
    font-weight: 700;
  }

  p {
    color: rgba($color-white, 0.72);
    font-size: 13px;
    line-height: 26px;
  }
}

.rule-item {
  display: grid;
  grid-template-columns: 28px minmax(0, 1fr);
  gap: 0;
  padding: 22px 0 24px;
  border-bottom: 1px solid rgba($color-primary, 0.17);
  scroll-margin-top: 0;
  transition: border-bottom-color 0.3s ease;

  &.is-selected {
    border-bottom-color: $color-primary;
  }

  &:first-child {
    padding-top: 0;
  }

  .rule-item-number {
    color: rgba($color-white, 0.75);
    font-size: 11px;
    line-height: 1.5;
    padding-top: 4px;
  }
  &.is-selected .rule-item-number {
    color: $color-primary;
  }

  h3 {
    margin-bottom: 12px;
    color: $color-white;
    font-size: 17px;
    font-weight: 700;
    line-height: 26px;
  }

  p {
    color: rgba($color-white, 0.72);
    font-size: 13px;
    line-height: 26px;

    & + p {
      margin-top: 8px;
    }
  }
}

@media screen and (max-width: $breakpoint-med) {
  .rule-content {
    padding: 54px 0 80px;
  }

  .rule-content-inner {
    width: calc(100% + 4%);
    margin-left: -2%;
  }

  .rule-navigation {
    position: static;

    .navigation-title {
      width: 125px;
      margin-left: auto;
      margin-right: auto;
      justify-content: center;
    }

    .navigation-toggle {
      display: flex;
      align-items: center;
      justify-content: space-between;
      width: 100%;
      min-height: 34px;
      padding: 0 10px;
      border: 0;
      border-radius: 2px;
      color: $color-white;
      background: #343434;
      font-size: 13px;
      line-height: 1.4;
      text-align: left;
      cursor: pointer;

      &:focus-visible {
        outline: 1px solid $color-primary;
        outline-offset: 2px;
      }
    }

    .navigation-toggle-icon {
      width: 7px;
      height: 7px;
      margin-right: 2px;
      border-right: 1px solid $color-white;
      border-bottom: 1px solid $color-white;
      transform: rotate(45deg) translateY(-2px);
      transition: transform 0.25s ease;
    }

    .navigation-toggle.is-open .navigation-toggle-icon {
      transform: rotate(225deg) translate(-1px, -1px);
    }

    nav {
      display: none;
      flex-direction: column;
      gap: 0;
      margin-top: 8px;
      padding: 6px 0;
      border: 1px solid rgba($color-white, 0.22);
      background: rgba($color-bg-dark, 0.82);

      &.is-open {
        display: flex;
      }
    }

    .navigation-link {
      grid-template-columns: 22px minmax(0, 1fr);
      gap: 2px;
      padding: 7px 9px;
      color: $color-white;
      font-size: 13px;
      line-height: 26px;

      span {
        color: $color-white;
        font-size: 11px;
      }

      &:hover,
      &:focus-visible,
      &.is-selected {
        color: $color-white;
        background: rgba($color-white, 0.07);
      }
    }
  }
}

@media screen and (max-width: $breakpoint-small) {
  .rule-content {
    padding: 40px 0 60px;
  }

  .rule-content-inner {
    width: calc(100% + 4%);
    margin-left: -2%;
  }

  .rule-intro h2 {
    font-size: 22px;
  }

  .rule-highlights {
    grid-template-columns: 1fr;
  }

  .highlight-card {
    min-height: auto;
    text-align: left;

    .highlight-number {
      display: inline-block;
      margin-right: 8px;
    }

    h3 {
      display: inline-block;
      margin: 0 0 5px;
    }
  }

  .rule-item {
    grid-template-columns: 24px minmax(0, 1fr);
  }
}
</style>
